import React, { useState } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlay,
  FaRocket,
  FaGamepad,
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import "../../styles/kids-program.css";

const GameDevelopment = () => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const modules = [
    {
      id: 1,
      title: "Introduction to Game Development",
      icon: "🎮",
      topics: [
        "What is Game Development?",
        "Types of games (2D, 3D, Mobile, PC)",
        "Introduction to Scratch platform",
        "Understanding game loops and mechanics",
        "Your first simple game",
      ],
    },
    {
      id: 2,
      title: "Game Design Basics",
      icon: "🎨",
      topics: [
        "Creating game characters (Sprites)",
        "Designing game backgrounds and levels",
        "Adding animations and effects",
        "Using sounds and music",
        "Making engaging user interfaces",
      ],
    },
    {
      id: 3,
      title: "Game Logic with Scratch",
      icon: "🧩",
      topics: [
        "Motion and movement blocks",
        "Conditionals and loops in games",
        "Variables and scoring systems",
        "Creating multiple levels",
        "Game state management",
      ],
    },
    {
      id: 4,
      title: "Building Different Game Types",
      icon: "🎯",
      topics: [
        "Platformer games (Mario-style)",
        "Racing games with timers",
        "Puzzle games and logic",
        "Shooting games with enemies",
        "Multiplayer game concepts",
      ],
    },
    {
      id: 5,
      title: "Advanced Game Features",
      icon: "⚡",
      topics: [
        "Adding power-ups and bonuses",
        "Creating boss battles",
        "Saving player progress",
        "Adding cutscenes and stories",
        "Polishing game mechanics",
      ],
    },
    {
      id: 6,
      title: "Publishing Your Game",
      icon: "🚀",
      topics: [
        "Testing and debugging",
        "Sharing games online",
        "Getting feedback from players",
        "Creating game portfolios",
        "Next steps: Unity and other engines",
      ],
    },
  ];

  const projects = [
    { name: "Platform Jump", icon: "🎮", description: "Create a classic platformer game with jumping mechanics" },
    { name: "Racing Game", icon: "🏎️", description: "Build a racing game with timers and obstacles" },
    { name: "Space Shooter", icon: "🚀", description: "Defend Earth from alien invaders" },
    { name: "Maze Puzzle", icon: "🧩", description: "Create challenging maze puzzles" },
  ];

  const faqs = [
    {
      q: "Do I need prior coding experience?",
      a: "No! Game development uses drag-and-drop block coding, perfect for beginners.",
    },
    {
      q: "What software do I need?",
      a: "You'll need a computer with internet access. All game development tools are web-based and free!",
    },
    {
      q: "Can I share my games with friends?",
      a: "Yes! You can share your games online and even get feedback from other young creators.",
    },
    {
      q: "Will my child get a certificate?",
      a: "Yes, every student receives a completion certificate after finishing the course.",
    },
    {
      q: "What age group is this suitable for?",
      a: "This course is designed for kids aged 10-18 who love playing and creating video games.",
    },
  ];

  const toggleModule = (id) => {
    setActiveModule(activeModule === id ? null : id);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleStartLearning = () => {
    document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWatchDemo = () => setShowDemoModal(true);
  const handleEnrollNow = () => navigate("/register");
  const handleBookDemo = () => navigate("/contact");
  const handleBackToKids = () => navigate("/kids");

  return (
    <div className="kids-program-page">
      <PublicNavbar />

      <section className="kids-program-hero kids-program-hero-orange">
        <div className="kids-program-hero-bubbles">
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
        </div>
        
        <Container>
          <Button variant="link" className="kids-program-back-btn" onClick={handleBackToKids}>
            <FaArrowLeft className="me-2" /> Back to Kids Programs
          </Button>

          <Row className="align-items-center">
            <Col lg={7}>
              <div className="kids-program-hero-content">
                <div className="kids-program-hero-badge">
                  <span>🎮</span><span>Create Your Own Video Games!</span><span>✨</span>
                </div>
                <h1 className="kids-program-hero-title">
                  Build Amazing <span>Video Games</span> with Drag & Drop!
                </h1>
                <p className="kids-program-hero-description">
                  Learn game design and development by creating your own video games. No coding experience needed - just your imagination!
                </p>
                <div className="kids-program-hero-buttons">
                  <Button className="kids-program-btn-primary" onClick={handleStartLearning}>
                    <FaRocket className="me-2" /> Start Learning
                  </Button>
                  <Button className="kids-program-btn-outline" onClick={handleWatchDemo}>
                    <FaPlay className="me-2" /> Watch Demo
                  </Button>
                </div>
                <div className="kids-program-hero-stats">
                  <div className="kids-program-stat"><span className="kids-program-stat-number">10+</span><span className="kids-program-stat-label">Games Created</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">0</span><span className="kids-program-stat-label">Code Required</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">100%</span><span className="kids-program-stat-label">Fun Guaranteed</span></div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="kids-program-hero-illustration">
                <div className="kids-program-floating-icon kids-program-icon-1">🎮</div>
                <div className="kids-program-floating-icon kids-program-icon-2">👾</div>
                <div className="kids-program-floating-icon kids-program-icon-3">🎯</div>
                <div className="kids-program-floating-icon kids-program-icon-4">🏆</div>
                <div className="kids-program-floating-icon kids-program-icon-5">⚡</div>
                <div className="kids-program-floating-icon kids-program-icon-6">🎨</div>
                <div className="kids-program-main-icon"><FaGamepad /></div>
              </div>
            </Col>
          </Row>
        </Container>

        <div className="kids-program-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      <section className="kids-program-learn">
        <Container>
          <div className="kids-program-section-header"><h2>What You'll Learn 🎯</h2><p>By the end of this course, you'll be able to</p></div>
          <Row className="g-4">
            {[
              { icon: "🎮", title: "Game Design", desc: "Create engaging game concepts and mechanics" },
              { icon: "🎨", title: "Visual Design", desc: "Design characters, backgrounds, and animations" },
              { icon: "🧩", title: "Game Logic", desc: "Add scoring, levels, and game rules" },
              { icon: "🚀", title: "Publish Games", desc: "Share your games with friends online" }
            ].map((item, i) => (
              <Col md={6} lg={3} key={i}>
                <div className="kids-program-learn-card">
                  <div className="kids-program-learn-icon kids-program-learn-icon-orange">{item.icon}</div>
                  <h4>{item.title}</h4><p>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="curriculum" className="kids-program-curriculum">
        <Container>
          <div className="kids-program-section-header"><h2>📚 Course Curriculum</h2><p>6 exciting modules to create amazing games</p></div>
          <div className="kids-program-modules">
            {modules.map((module) => (
              <div key={module.id} className={`kids-program-module kids-program-module-orange ${activeModule === module.id ? "active" : ""}`}>
                <div className="kids-program-module-header" onClick={() => toggleModule(module.id)}>
                  <h3><span className="kids-program-module-icon">{module.icon}</span>Module {module.id}: {module.title}</h3>
                  <span className="kids-program-module-toggle">{activeModule === module.id ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>
                <div className="kids-program-module-content">
                  <ul>{module.topics.map((topic, idx) => (<li key={idx}><FaCheckCircle className="me-2" style={{ color: "#FF9800" }} />{topic}</li>))}</ul>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-projects">
        <Container>
          <div className="kids-program-section-header"><h2>🎮 Games You'll Create</h2><p>Build these exciting games and more!</p></div>
          <Row className="g-4">{projects.map((p, i) => (<Col md={6} lg={3} key={i}><div className="kids-program-project-card"><div className="kids-program-project-icon">{p.icon}</div><h4>{p.name}</h4><p>{p.description}</p></div></Col>))}</Row>
        </Container>
      </section>

      <section className="kids-program-why kids-program-why-orange">
        <Container>
          <div className="kids-program-section-header text-white"><h2 className="text-white">Why Choose This Course? 🌟</h2><p className="text-white">The perfect start for future game developers</p></div>
          <div className="kids-program-why-grid">
            {[["🎯", "No Coding Needed", "Drag-and-drop game creation"], ["🎮", "10+ Games", "Create multiple game genres"], ["🏆", "Certificate", "Get certified on completion"], ["🎨", "Creative Freedom", "Design games your way"]].map((item, i) => (
              <div key={i} className="kids-program-why-item"><div className="kids-program-why-icon">{item[0]}</div><h4>{item[1]}</h4><p>{item[2]}</p></div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-faq">
        <Container>
          <div className="kids-program-section-header"><h2>❓ Frequently Asked Questions</h2><p>Everything parents need to know</p></div>
          <div className="kids-program-faq-container">{faqs.map((faq, idx) => (<div key={idx} className={`kids-program-faq-item ${openFaqIndex === idx ? "active" : ""}`}><div className="kids-program-faq-question" onClick={() => toggleFaq(idx)}><span>{faq.q}</span><span>{openFaqIndex === idx ? <FaChevronUp /> : <FaChevronDown />}</span></div><div className="kids-program-faq-answer"><p>{faq.a}</p></div></div>))}</div>
        </Container>
      </section>

      <section className="kids-program-cta">
        <Container><h2>🎮 Ready to Create Your First Video Game?</h2><p>Join young game developers who are already creating amazing games!</p><div className="kids-program-cta-buttons"><Button className="kids-program-cta-primary" onClick={handleEnrollNow}>Enroll Now ✨</Button><Button className="kids-program-cta-secondary" onClick={handleBookDemo}>Book Free Demo 📅</Button></div></Container>
      </section>

      <Modal show={showDemoModal} onHide={() => setShowDemoModal(false)} size="lg" centered className="kids-program-modal">
        <Modal.Header closeButton><Modal.Title><FaPlay className="me-2" /> Game Development Demo</Modal.Title></Modal.Header>
        <Modal.Body className="p-0"><div className="video-wrapper"><iframe src="https://www.youtube.com/embed/QnubPziozsw" title="Demo" frameBorder="0" allowFullScreen></iframe></div></Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowDemoModal(false)}>Close</Button></Modal.Footer>
      </Modal>
      <Footer />
    </div>
  );
};

export default GameDevelopment;